import { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronLeft, CheckCircle, AlertTriangle, Clock, Circle, Shield, FileText, Wrench, Smartphone, Users, Gavel, X, ClipboardList, Upload, Sparkles, Award, ExternalLink, Link2, Paperclip, Eye, Trash2 } from 'lucide-react'
import api from '../../services/api'

// ── Constants ──────────────────────────────────────────────────────────────────

const TIMING_LABELS: Record<string, string> = {
  before_day1: 'Before Day 1', day1: 'Day 1', week1: 'Week 1', month1: 'Month 1', ongoing: 'Ongoing',
}

const CAT_META: Record<string, { label: string; icon: any; colour: string }> = {
  legal:        { label: 'Legal / Compliance',     icon: Gavel,      colour: 'var(--red)' },
  documentation:{ label: 'Documentation',          icon: FileText,   colour: 'var(--blue)' },
  safety:       { label: 'Safety Induction',       icon: Shield,     colour: 'var(--green)' },
  role_training:{ label: 'Role Specific Training', icon: Wrench,     colour: 'var(--hseq-safety)' },
  system_access:{ label: 'System Access',          icon: Smartphone, colour: 'var(--purple)' },
  meet_team:    { label: 'Meet the Team',          icon: Users,      colour: 'var(--teal)' },
}

const TIMING_ORDER = ['before_day1', 'day1', 'week1', 'month1', 'ongoing']

// ── Item completion modal ──────────────────────────────────────────────────────

type CompetencyDraft = {
  type: string; name: string; licence_number: string; licence_class: string;
  issuing_authority: string; issue_date: string; expiry_date: string; ai_extracted: boolean;
}

const EMPTY_COMP: CompetencyDraft = {
  type: 'licence', name: '', licence_number: '', licence_class: '',
  issuing_authority: '', issue_date: '', expiry_date: '', ai_extracted: false,
}

function CompleteModal({ item, workerId, contractorOnboarded, onSave, onClose }: {
  item: any
  workerId: string
  contractorOnboarded: boolean
  onSave: (note: string, competency?: CompetencyDraft) => void
  onClose: () => void
}) {
  const [note, setNote] = useState('')
  const [compStep, setCompStep] = useState<'main' | 'extracting' | 'review'>('main')
  const [comp, setComp] = useState<CompetencyDraft>({ ...EMPTY_COMP, name: item.name })
  const [extractError, setExtractError] = useState('')
  const [uploads, setUploads] = useState<any[]>(item.uploads || [])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const compFileRef = useRef<HTMLInputElement>(null)

  const needsUpload = item.upload_required && !item.upload_bypassed
  const isCompetency = item.links_to_competency === 1
  const canComplete = !needsUpload || uploads.length > 0

  const uploadFile = async (file: File) => {
    setUploading(true)
    setUploadError('')
    try {
      const form = new FormData()
      form.append('file', file)
      const { data } = await api.post(
        `/onboarding/workers/${workerId}/items/${item.id}/upload`,
        form, { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      setUploads(prev => [...prev, data])
    } catch (e: any) {
      setUploadError(e.response?.data?.error || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const removeUpload = async (uploadId: string) => {
    try {
      await api.delete(`/onboarding/workers/${workerId}/items/${item.id}/uploads/${uploadId}`)
      setUploads(prev => prev.filter(u => u.id !== uploadId))
    } catch {}
  }

  const handleCompFile = async (file: File) => {
    setCompStep('extracting')
    setExtractError('')
    const reader = new FileReader()
    reader.onload = async (e) => {
      const base64 = (e.target?.result as string).split(',')[1]
      try {
        const { data } = await api.post('/onboarding/extract-document', {
          image_base64: base64, mime_type: file.type || 'image/jpeg', item_name: item.name,
        })
        setComp({
          type: data.competency_type || 'licence', name: data.competency_name || item.name,
          licence_number: data.licence_number || '', licence_class: data.licence_class || '',
          issuing_authority: data.issuing_authority || '',
          issue_date: data.issue_date || '', expiry_date: data.expiry_date || '',
          ai_extracted: true,
        })
        if (!note && data.holder_name) setNote(`Holder: ${data.holder_name}`)
        setCompStep('review')
      } catch {
        setExtractError('AI extraction failed — enter details manually')
        setCompStep('review')
      }
    }
    reader.readAsDataURL(file)
  }

  const handleSubmit = () => onSave(note, isCompetency ? comp : undefined)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--white)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
        width: '100%', maxWidth: 500, padding: 24, display: 'flex', flexDirection: 'column', gap: 16,
        maxHeight: '92vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Complete Item</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
        </div>

        <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
            <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', margin: 0, flex: 1 }}>{item.name}</p>
            {item.blocking === 1 && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--red-light)', color: 'var(--red)', fontWeight: 700, flexShrink: 0 }}>BLOCKING</span>}
            {item.sighting_only === 1 && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--amber-light)', color: 'var(--amber)', fontWeight: 600, flexShrink: 0 }}>sighting only</span>}
          </div>
          {item.description && <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>{item.description}</p>}
          {item.upload_bypassed && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <CheckCircle size={11} /> Upload not required — onboarded contractor
            </div>
          )}
        </div>

        {/* File upload section */}
        {needsUpload && (
          <div style={{ border: `1.5px solid ${uploads.length > 0 ? 'var(--green)' : 'var(--amber)'}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '8px 14px', background: uploads.length > 0 ? 'var(--green-light)' : 'var(--amber-light)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Paperclip size={13} style={{ color: uploads.length > 0 ? 'var(--green)' : 'var(--amber)', flexShrink: 0 }} />
              <p style={{ fontSize: 12, fontWeight: 700, color: uploads.length > 0 ? 'var(--green)' : 'var(--amber)', margin: 0 }}>
                {uploads.length > 0 ? `${uploads.length} file${uploads.length !== 1 ? 's' : ''} uploaded` : 'Document upload required'}
              </p>
              {item.sighting_only === 1 && <span style={{ fontSize: 10, color: uploads.length > 0 ? 'var(--green)' : 'var(--amber)', marginLeft: 'auto', opacity: 0.8 }}>sighting only — not stored permanently</span>}
            </div>
            <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {uploads.map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <FileText size={13} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.original_name}</span>
                  <a href={`/api/onboarding/uploads/${u.id}/file`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', display: 'flex' }}><Eye size={13} /></a>
                  <button onClick={() => removeUpload(u.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex' }}><Trash2 size={13} /></button>
                </div>
              ))}
              <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                onChange={e => { if (e.target.files?.[0]) { uploadFile(e.target.files[0]); e.target.value = '' } }} />
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) uploadFile(f) }}
                onClick={() => !uploading && fileRef.current?.click()}
                style={{
                  border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8,
                  padding: '12px 10px', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer',
                  background: dragOver ? 'var(--accent-light)' : 'var(--bg)',
                }}
              >
                {uploading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Uploading…</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <Upload size={14} style={{ color: 'var(--text-muted)' }} />
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Drop file or click to browse · JPG, PNG, PDF</span>
                  </div>
                )}
              </div>
              {uploadError && <p style={{ fontSize: 11, color: 'var(--red)', margin: 0 }}>{uploadError}</p>}
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
            {item.evidence_type === 'signoff' ? 'SIGN-OFF DETAILS' : item.evidence_type !== 'none' ? 'NOTES / EVIDENCE REFERENCE' : 'NOTES (optional)'}
          </label>
          <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
            placeholder={
              item.evidence_type === 'signoff' ? 'Name and role of person signing off…' :
              item.sighting_only === 1 ? 'Note who sighted the document and when…' :
              'Any notes about completion…'
            }
            style={{ width: '100%', resize: 'none', boxSizing: 'border-box' }} />
        </div>

        {/* Competency section */}
        {isCompetency && (
          <div style={{ border: '1px solid var(--green)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '8px 14px', background: 'var(--green-light)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Award size={13} style={{ color: 'var(--green)', flexShrink: 0 }} />
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', margin: 0 }}>Competency Record</p>
              <span style={{ fontSize: 11, color: 'var(--green)', marginLeft: 'auto', opacity: 0.8 }}>Saved to worker profile</span>
            </div>
            <input ref={compFileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
              onChange={e => { if (e.target.files?.[0]) { handleCompFile(e.target.files[0]); e.target.value = '' } }} />
            {compStep === 'main' && (
              <div style={{ padding: '12px 14px', display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" style={{ flex: 1, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                  onClick={() => compFileRef.current?.click()}>
                  <Sparkles size={13} style={{ color: 'var(--green)' }} /> AI Extract
                </button>
                <button className="btn btn-secondary" style={{ flex: 1, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                  onClick={() => setCompStep('review')}>
                  <FileText size={13} /> Manual Entry
                </button>
              </div>
            )}
            {compStep === 'extracting' && (
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid var(--green)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', margin: '0 auto 8px' }} />
                <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Reading document with AI…</p>
              </div>
            )}
            {compStep === 'review' && (
              <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {comp.ai_extracted && <div style={{ fontSize: 11, color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 4 }}><Sparkles size={11} /> AI extracted — review and correct if needed</div>}
                {extractError && <div style={{ fontSize: 11, color: 'var(--amber)', padding: '4px 8px', borderRadius: 6, background: 'var(--amber-light)' }}>{extractError}</div>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>TYPE</label>
                    <select value={comp.type} onChange={e => setComp((c: any) => ({ ...c, type: e.target.value }))} style={{ width: '100%', fontSize: 12 }}>
                      {['licence','certificate','induction','training','whs_training','first_aid','other'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>COMPETENCY NAME</label>
                    <input value={comp.name} onChange={e => setComp((c: any) => ({ ...c, name: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', fontSize: 12 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>LICENCE NUMBER</label>
                    <input value={comp.licence_number} onChange={e => setComp((c: any) => ({ ...c, licence_number: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', fontSize: 12 }} placeholder="e.g. WHS-12345" />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>CLASS</label>
                    <input value={comp.licence_class} onChange={e => setComp((c: any) => ({ ...c, licence_class: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', fontSize: 12 }} placeholder="e.g. C, DG" />
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>ISSUING AUTHORITY</label>
                    <input value={comp.issuing_authority} onChange={e => setComp((c: any) => ({ ...c, issuing_authority: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', fontSize: 12 }} placeholder="e.g. SafeWork NSW" />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>ISSUE DATE</label>
                    <input type="date" value={comp.issue_date} onChange={e => setComp((c: any) => ({ ...c, issue_date: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', fontSize: 12 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>EXPIRY DATE</label>
                    <input type="date" value={comp.expiry_date} onChange={e => setComp((c: any) => ({ ...c, expiry_date: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', fontSize: 12 }} />
                  </div>
                </div>
                <button style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={() => compFileRef.current?.click()}>
                  <Upload size={11} /> Re-upload for AI extraction
                </button>
              </div>
            )}
          </div>
        )}

        {!canComplete && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--amber-light)', border: '1px solid var(--amber)', fontSize: 12, color: 'var(--amber)', fontWeight: 600 }}>
            Upload at least one document before marking complete
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!canComplete}
            style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onClick={handleSubmit}>
            <CheckCircle size={15} /> Mark Complete
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Assign template modal ──────────────────────────────────────────────────────

function AssignModal({ workerId, templates, onSave, onClose }: {
  workerId: string; templates: any[]; onSave: () => void; onClose: () => void
}) {
  const [form, setForm] = useState({ template_id: '', start_date: new Date().toISOString().slice(0, 10) })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const save = async () => {
    if (!form.template_id) { setError('Select a template'); return }
    setSaving(true)
    try {
      await api.post(`/onboarding/workers/${workerId}/assign`, form)
      onSave()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--white)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
        width: '100%', maxWidth: 420, padding: 24, display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>Assign Onboarding Template</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>TEMPLATE</label>
          <select value={form.template_id} onChange={e => setForm(f => ({ ...f, template_id: e.target.value }))} style={{ width: '100%' }}>
            <option value="">— Select —</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>START DATE</label>
          <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box' }} />
        </div>
        {error && <p style={{ fontSize: 13, color: 'var(--red)' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 2 }} onClick={save} disabled={saving}>{saving ? 'Assigning…' : 'Assign'}</button>
        </div>
      </div>
    </div>
  )
}

// ── Upload link modal ──────────────────────────────────────────────────────────

function UploadLinkModal({ workerId, onClose }: { workerId: string; onClose: () => void }) {
  const [uploaderType, setUploaderType] = useState<'contractor' | 'worker'>('contractor')
  const [uploaderName, setUploaderName] = useState('')
  const [generating, setGenerating] = useState(false)
  const [link, setLink] = useState('')
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')

  const generate = async () => {
    setGenerating(true)
    setError('')
    try {
      const { data } = await api.post(`/onboarding/workers/${workerId}/generate-upload-link`, {
        uploaded_by_type: uploaderType,
        uploaded_by_name: uploaderName || null,
      })
      setLink(data.url)
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to generate link')
    } finally {
      setGenerating(false)
    }
  }

  const copy = () => {
    navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--white)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
        width: '100%', maxWidth: 440, padding: 24, display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Send Upload Link</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
          Generate a secure link that the contractor or worker can use to upload their documents directly — no login required. Link expires in 7 days.
        </p>

        {!link ? (
          <>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>WHO IS UPLOADING?</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['contractor', 'worker'] as const).map(t => (
                  <div key={t} onClick={() => setUploaderType(t)} style={{
                    flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                    border: `1.5px solid ${uploaderType === t ? 'var(--accent)' : 'var(--border)'}`,
                    background: uploaderType === t ? 'var(--accent-light)' : 'var(--bg)',
                  }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: uploaderType === t ? 'var(--accent)' : 'var(--text)', margin: 0, textTransform: 'capitalize' }}>{t}</p>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>
                      {t === 'contractor' ? 'Company representative' : 'Individual worker'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>UPLOADER NAME (optional)</label>
              <input value={uploaderName} onChange={e => setUploaderName(e.target.value)}
                placeholder={uploaderType === 'contractor' ? 'e.g. Acme Electrical Pty Ltd' : 'e.g. John Smith'}
                style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            {error && <p style={{ fontSize: 12, color: 'var(--red)', margin: 0 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                onClick={generate} disabled={generating}>
                <Link2 size={14} /> {generating ? 'Generating…' : 'Generate Link'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--green-light)', border: '1px solid var(--green)' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', margin: '0 0 6px' }}>UPLOAD LINK READY</p>
              <p style={{ fontSize: 12, color: 'var(--text)', margin: 0, wordBreak: 'break-all', fontFamily: 'monospace' }}>{link}</p>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
              Copy this link and send it via email, SMS, or your preferred channel. The recipient can upload directly — no account needed.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Close</button>
              <button className="btn btn-primary" style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                onClick={copy}>
                {copied ? <><CheckCircle size={14} /> Copied!</> : <><Link2 size={14} /> Copy Link</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Main dashboard ─────────────────────────────────────────────────────────────

export default function OnboardingDashboard() {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<{ worker: any; onboarding: any; items: any[]; contractor_onboarded?: boolean } | null>(null)
  const [templates, setTemplates] = useState<any[]>([])
  const [qualifications, setQualifications] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [completeItem, setCompleteItem] = useState<any>(null)
  const [showAssign, setShowAssign] = useState(false)
  const [showUploadLink, setShowUploadLink] = useState(false)
  const [newCompetency, setNewCompetency] = useState<any>(null)

  const load = () => {
    setLoading(true)
    Promise.all([
      api.get(`/onboarding/workers/${id}`),
      api.get('/onboarding/templates').catch(() => ({ data: { data: [] } })),
      api.get(`/onboarding/workers/${id}/qualifications`).catch(() => ({ data: { data: [] } })),
    ]).then(([d, t, q]) => {
      setData(d.data)
      setTemplates(t.data.data)
      setQualifications(q.data.data || [])
    }).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [id])

  const markComplete = async (note: string, competency?: any) => {
    if (!completeItem) return
    const res = await api.patch(`/onboarding/workers/${id}/items/${completeItem.id}`, {
      status: 'completed',
      evidence_note: note,
      competency,
    })
    setCompleteItem(null)
    if (res.data?.competency) setNewCompetency(res.data.competency)
    load()
  }

  if (loading) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading…</div>
  if (!data) return <div className="text-center py-16" style={{ color: 'var(--red)' }}>Worker not found</div>

  const { worker, onboarding, items } = data

  // Aggregate stats
  const totalItems = items.length
  const doneItems = items.filter(i => i.status === 'completed' || i.status === 'waived').length
  const blockingPending = items.filter(i => i.blocking === 1 && i.status === 'pending')
  const blockingOverdue = blockingPending.filter(i => i.is_overdue)
  const overdueItems = items.filter(i => i.is_overdue)
  const dueTodayItems = items.filter(i => i.is_due_today)
  const pct = totalItems ? Math.round((doneItems / totalItems) * 100) : 0
  const canWork = blockingPending.length === 0

  // Group by timing
  const grouped: Record<string, any[]> = {}
  TIMING_ORDER.forEach(t => { grouped[t] = [] })
  items.forEach(i => { const t = i.due_timing || 'day1'; if (!grouped[t]) grouped[t] = []; grouped[t].push(i) })

  // Category progress
  const cats = Object.keys(CAT_META)
  const catStats = cats.map(cat => {
    const catItems = items.filter(i => i.category === cat)
    const catDone = catItems.filter(i => i.status === 'completed' || i.status === 'waived').length
    return { cat, total: catItems.length, done: catDone }
  }).filter(c => c.total > 0)

  const statusColour = !onboarding ? 'var(--text-muted)' :
    blockingOverdue.length > 0 ? 'var(--red)' :
    pct === 100 ? 'var(--green)' :
    overdueItems.length > 0 ? 'var(--amber)' : 'var(--accent)'

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Back */}
      <Link to="/workforce" className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--accent)' }}>
        <ChevronLeft size={16} /> Workforce
      </Link>

      {/* Worker header */}
      <div className="card">
        <div className="flex items-start gap-4">
          <div className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0" style={{ width: 52, height: 52, fontSize: 18, background: canWork && onboarding ? 'var(--green)' : blockingPending.length > 0 ? 'var(--red)' : 'var(--accent)' }}>
            {worker.first_name[0]}{worker.last_name[0]}
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>{worker.first_name} {worker.last_name}</h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {[worker.job_title, worker.department, worker.employment_type?.replace('_', ' ')].filter(Boolean).join(' · ')}
            </p>
            {onboarding && (
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Started {new Date(onboarding.start_date).toLocaleDateString('en-AU')} · {onboarding.template_name}
              </p>
            )}
          </div>
          <div className="text-right flex-shrink-0" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            {onboarding ? (
              <>
                <p className="text-3xl font-bold" style={{ color: statusColour }}>{pct}%</p>
                <p className="text-xs" style={{ color: statusColour }}>
                  {pct === 100 ? 'Fully Onboarded' : blockingPending.length > 0 ? `${blockingPending.length} blocking` : 'In Progress'}
                </p>
                <button onClick={() => setShowUploadLink(true)} className="btn btn-secondary"
                  style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', minHeight: 'auto' }}>
                  <Link2 size={13} /> Send Upload Link
                </button>
              </>
            ) : (
              <button onClick={() => setShowAssign(true)} className="btn btn-primary text-sm">Assign Checklist</button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {onboarding && (
          <div style={{ marginTop: 16 }}>
            <div style={{ height: 8, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: statusColour, borderRadius: 4, transition: 'width 0.4s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
              <span>{doneItems} of {totalItems} items completed</span>
              <button onClick={() => setShowAssign(true)} className="text-xs underline" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>Change template</button>
            </div>
          </div>
        )}
      </div>

      {!onboarding && (
        <div className="card p-8 text-center">
          <ClipboardList size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-semibold mb-1" style={{ color: 'var(--text)' }}>No onboarding checklist assigned</p>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>Assign an onboarding template to get started.</p>
          <button onClick={() => setShowAssign(true)} className="btn btn-primary">Assign Checklist</button>
        </div>
      )}

      {onboarding && (
        <>
          {/* Work clearance banner */}
          {blockingPending.length > 0 ? (
            <div style={{ padding: '12px 16px', borderRadius: 10, background: 'var(--red-light)', border: '1px solid var(--red)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <AlertTriangle size={18} style={{ color: 'var(--red)', flexShrink: 0, marginTop: 2 }} />
              <div>
                <p style={{ fontWeight: 700, fontSize: 14, color: 'var(--red)', margin: 0 }}>Cannot be assigned to work — {blockingPending.length} blocking item{blockingPending.length !== 1 ? 's' : ''} incomplete</p>
                <p style={{ fontSize: 12, color: 'var(--red)', margin: '4px 0 0', opacity: 0.8 }}>{blockingPending.map(i => i.name).join(', ')}</p>
              </div>
            </div>
          ) : (
            <div style={{ padding: '10px 16px', borderRadius: 10, background: 'var(--green-light)', border: '1px solid var(--green)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <CheckCircle size={16} style={{ color: 'var(--green)', flexShrink: 0 }} />
              <p style={{ fontWeight: 600, fontSize: 13, color: 'var(--green)', margin: 0 }}>All blocking items complete — cleared for work assignment</p>
            </div>
          )}

          {/* Two-column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, alignItems: 'start' }}>
            {/* Checklist */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {TIMING_ORDER.map(timing => {
                const grpItems = grouped[timing]
                if (!grpItems?.length) return null
                return (
                  <div key={timing}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      {TIMING_LABELS[timing]}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {grpItems.map(item => {
                        const done = item.status === 'completed' || item.status === 'waived'
                        const isOverdue = item.is_overdue
                        const isDueToday = item.is_due_today
                        const catMeta = CAT_META[item.category] || CAT_META.safety
                        const CatIcon = catMeta.icon

                        return (
                          <div key={item.id} style={{
                            padding: '10px 14px', borderRadius: 10,
                            background: done ? 'var(--bg)' : item.blocking === 1 && !done ? (isOverdue ? 'var(--red-light)' : 'var(--white)') : 'var(--white)',
                            border: `1px solid ${isOverdue && !done ? 'var(--red)' : done ? 'var(--border)' : item.blocking === 1 ? 'var(--red)' : 'var(--border)'}`,
                            opacity: done ? 0.7 : 1,
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                          }}>
                            <div style={{ flexShrink: 0, marginTop: 1 }}>
                              {done ? (
                                <CheckCircle size={18} style={{ color: 'var(--green)' }} />
                              ) : isOverdue ? (
                                <AlertTriangle size={18} style={{ color: 'var(--red)' }} />
                              ) : isDueToday ? (
                                <Clock size={18} style={{ color: 'var(--amber)' }} />
                              ) : (
                                <Circle size={18} style={{ color: 'var(--border)' }} />
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <p style={{ fontSize: 13, fontWeight: done ? 400 : 600, color: done ? 'var(--text-secondary)' : 'var(--text)', margin: 0, textDecoration: done ? 'line-through' : 'none' }}>
                                  {item.name}
                                </p>
                                {item.blocking === 1 && !done && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--red-light)', color: 'var(--red)', fontWeight: 700 }}>BLOCKING</span>}
                                {item.links_to_competency === 1 && !done && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--green-light)', color: 'var(--green)', fontWeight: 600 }}>→ competency</span>}
                                {item.sighting_only === 1 && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--amber-light)', color: 'var(--amber)', fontWeight: 600 }}>sighting</span>}
                                {item.upload_required && !done && item.upload_count > 0 && (
                                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--green-light)', color: 'var(--green)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Paperclip size={9} /> {item.upload_count}
                                  </span>
                                )}
                                {item.upload_required && !done && item.upload_count === 0 && !item.upload_bypassed && (
                                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--amber-light)', color: 'var(--amber)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Paperclip size={9} /> upload needed
                                  </span>
                                )}
                              </div>
                              {item.description && <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '3px 0 0' }}>{item.description}</p>}
                              <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                                  <CatIcon size={11} /> {catMeta.label}
                                </span>
                                {item.due_date && !done && (
                                  <span style={{ fontSize: 11, color: isOverdue ? 'var(--red)' : isDueToday ? 'var(--amber)' : 'var(--text-muted)', fontWeight: isOverdue || isDueToday ? 600 : 400 }}>
                                    {isOverdue ? 'Overdue — ' : isDueToday ? 'Due today — ' : 'Due '}
                                    {new Date(item.due_date).toLocaleDateString('en-AU')}
                                  </span>
                                )}
                                {done && item.completed_at && (
                                  <span style={{ fontSize: 11, color: 'var(--green)' }}>Completed {new Date(item.completed_at).toLocaleDateString('en-AU')}</span>
                                )}
                                {done && item.upload_count > 0 && (
                                  <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Paperclip size={10} /> {item.upload_count} file{item.upload_count !== 1 ? 's' : ''}
                                  </span>
                                )}
                                {done && item.evidence_note && (
                                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>{item.evidence_note}</span>
                                )}
                              </div>
                            </div>
                            {!done && (
                              <button
                                onClick={() => setCompleteItem(item)}
                                className="btn btn-secondary text-xs px-3 py-1.5 flex-shrink-0"
                                style={{ minHeight: 'auto', fontSize: 12, whiteSpace: 'nowrap' }}
                              >
                                {isDueToday || isOverdue ? '⚡ Complete now' : 'Complete'}
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Right panel — category progress */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="card" style={{ padding: 16 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>Progress by Category</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {catStats.map(({ cat, total, done }) => {
                    const meta = CAT_META[cat]
                    const Icon = meta.icon
                    const p = total ? Math.round((done / total) * 100) : 0
                    return (
                      <div key={cat}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Icon size={11} style={{ color: meta.colour }} /> {meta.label}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: p === 100 ? 'var(--green)' : 'var(--text-muted)' }}>{done}/{total}</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${p}%`, background: p === 100 ? 'var(--green)' : meta.colour, borderRadius: 3 }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Alerts */}
              {overdueItems.length > 0 && (
                <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--red-light)', border: '1px solid var(--red)' }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--red)', margin: '0 0 6px' }}>
                    <AlertTriangle size={12} style={{ display: 'inline', marginRight: 4 }} />
                    {overdueItems.length} Overdue
                  </p>
                  {overdueItems.slice(0, 3).map(i => (
                    <p key={i.id} style={{ fontSize: 11, color: 'var(--red)', margin: '2px 0', opacity: 0.85 }}>· {i.name}</p>
                  ))}
                </div>
              )}
              {dueTodayItems.length > 0 && (
                <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--amber-light)', border: '1px solid var(--amber)' }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--amber)', margin: '0 0 6px' }}>
                    <Clock size={12} style={{ display: 'inline', marginRight: 4 }} />
                    Due Today
                  </p>
                  {dueTodayItems.map(i => (
                    <p key={i.id} style={{ fontSize: 11, color: 'var(--amber)', margin: '2px 0' }}>· {i.name}</p>
                  ))}
                </div>
              )}

              {/* Qualifications panel */}
              <div className="card" style={{ padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Award size={14} style={{ color: 'var(--green)' }} /> Qualifications
                  </p>
                  <Link to="/competencies" style={{ fontSize: 11, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 3 }}>
                    View all <ExternalLink size={10} />
                  </Link>
                </div>
                {qualifications.length === 0 ? (
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>No competency records yet</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {qualifications.map(q => (
                      <div key={q.id} style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--bg)', border: `1px solid ${q.expiry_status === 'expired' ? 'var(--red)' : q.expiry_status === 'expiring_soon' ? 'var(--amber)' : 'var(--border)'}` }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', margin: 0, lineHeight: 1.3 }}>{q.competency_name}</p>
                          {q.onboarding_item_id && (
                            <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--green-light)', color: 'var(--green)', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>onboarding</span>
                          )}
                        </div>
                        {q.licence_number && <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '2px 0 0', fontFamily: 'monospace' }}>{q.licence_number}</p>}
                        {q.issuing_authority && <p style={{ fontSize: 10, color: 'var(--text-muted)', margin: '2px 0 0' }}>{q.issuing_authority}</p>}
                        {q.expiry_date && (
                          <p style={{ fontSize: 10, margin: '3px 0 0', fontWeight: 600, color: q.expiry_status === 'expired' ? 'var(--red)' : q.expiry_status === 'expiring_soon' ? 'var(--amber)' : 'var(--text-muted)' }}>
                            {q.expiry_status === 'expired' ? 'Expired' : q.expiry_status === 'expiring_soon' ? 'Expiring' : 'Expires'} {new Date(q.expiry_date).toLocaleDateString('en-AU')}
                          </p>
                        )}
                        {q.ai_extracted === 1 && (
                          <span style={{ fontSize: 10, color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 3, marginTop: 3 }}><Sparkles size={9} /> AI extracted</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* New competency success banner */}
      {newCompetency && (
        <div
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 100,
            padding: '14px 18px', borderRadius: 12, background: 'var(--green)', color: '#fff',
            boxShadow: 'var(--shadow-lg)', display: 'flex', alignItems: 'center', gap: 10, maxWidth: 320,
          }}
        >
          <Award size={18} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 700, fontSize: 13, margin: 0 }}>Competency record created</p>
            <p style={{ fontSize: 12, margin: '2px 0 0', opacity: 0.9 }}>{newCompetency.competency_name}</p>
          </div>
          <button onClick={() => setNewCompetency(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', opacity: 0.8 }}><X size={14} /></button>
        </div>
      )}

      {completeItem && (
        <CompleteModal
          item={completeItem}
          workerId={id!}
          contractorOnboarded={data?.contractor_onboarded ?? false}
          onSave={markComplete}
          onClose={() => setCompleteItem(null)}
        />
      )}
      {showAssign && (
        <AssignModal
          workerId={id!}
          templates={templates}
          onSave={() => { setShowAssign(false); load() }}
          onClose={() => setShowAssign(false)}
        />
      )}
      {showUploadLink && (
        <UploadLinkModal workerId={id!} onClose={() => setShowUploadLink(false)} />
      )}
    </div>
  )
}
